import { ReactNode, useEffect, useState } from "react";
import { HostPortalContext, HostPortalContextType } from "./context";
import { portalBrandingService } from "@/services/portalBranding.service";
import {
  currentPortalHost,
  hostnameOf,
  isLocalHostname,
} from "@/lib/portalHost";

// Versioned: bump when HostPortalBranding gains fields, so a stale cached
// shape from an earlier build is refetched rather than half-used.
const CACHE_PREFIX = "portalBranding.v5:";

/**
 * Session-cached lookup so a branded portal doesn't flash the default
 * branding on every navigation. `undefined` means "never resolved this
 * host"; `null` is a cached "not a portal".
 */
/** What we cache per host: the settled resolution, minus loading state. */
type CachedHost = Pick<
  HostPortalContextType,
  "branding" | "isUnknownPortal" | "unknownSlug"
>;

function readCache(hostname: string): CachedHost | undefined {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + hostname);
    if (raw === null) return undefined;
    return JSON.parse(raw) as CachedHost;
  } catch {
    return undefined;
  }
}

function writeCache(hostname: string, value: CachedHost) {
  try {
    sessionStorage.setItem(CACHE_PREFIX + hostname, JSON.stringify(value));
  } catch {
    // Private-mode storage failures just cost a refetch next load.
  }
}

const MARKETPLACE: CachedHost = {
  branding: null,
  isUnknownPortal: false,
  unknownSlug: null,
};

interface PortalBrandingProviderProps {
  children: ReactNode;
}

/**
 * Resolves white-label branding from the page hostname, before any login.
 * On the marketplace apex (and in dev) this settles immediately to "not
 * branded"; on a `<slug>.reveshare.com` portal it fetches the brand's public
 * config once and caches it for the session.
 */
export function PortalBrandingProvider({
  children,
}: PortalBrandingProviderProps) {
  // The host to brand from: the page host, or the local dev override that
  // lets a portal be previewed on localhost (see lib/portalHost).
  const [portalHost] = useState(currentPortalHost);

  const [state, setState] = useState<HostPortalContextType>(() => {
    const hostname = hostnameOf(portalHost);
    if (isLocalHostname(hostname)) return { isLoading: false, ...MARKETPLACE };
    const cached = readCache(hostname);
    if (cached !== undefined) return { isLoading: false, ...cached };
    return { isLoading: true, ...MARKETPLACE };
  });

  useEffect(() => {
    if (!state.isLoading) return;
    let cancelled = false;

    portalBrandingService
      .resolveByHost(portalHost)
      .then((resolution) => {
        let settled: CachedHost;
        if (resolution.kind === "PORTAL") {
          settled = {
            branding: {
              brandDetailId: resolution.brandDetailId,
              brandUsername: resolution.brandUsername,
              // The brand's own name is the portal's name.
              portalName: resolution.brandName ?? "",
              portalLogo: resolution.portalLogo,
              primaryColour: resolution.primaryColour ?? null,
              secondaryColour: resolution.secondaryColour ?? null,
            },
            isUnknownPortal: false,
            unknownSlug: null,
          };
        } else if (resolution.kind === "UNKNOWN_PORTAL") {
          settled = {
            branding: null,
            isUnknownPortal: true,
            unknownSlug: resolution.portalSlug,
          };
        } else {
          settled = MARKETPLACE;
        }
        writeCache(hostnameOf(portalHost), settled);
        if (!cancelled) setState({ isLoading: false, ...settled });
      })
      .catch(() => {
        // A network failure is not evidence that the brand does not exist, so
        // fall back to the marketplace rather than accusing a real portal of
        // being unknown. Not cached, so the next load retries.
        if (!cancelled) setState({ isLoading: false, ...MARKETPLACE });
      });

    return () => {
      cancelled = true;
    };
    // Runs once: isLoading only ever goes true -> false.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <HostPortalContext.Provider value={state}>
      {children}
    </HostPortalContext.Provider>
  );
}
